import React, { useState, useRef } from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Upload, X, FileText, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Milestone } from '../../types/contract';
import { useToast } from '../../components/UI/Toast';
import { mediaUploadService, UploadProgress } from '../../services/mediaUploadService';

interface DeliverableSubmissionProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: Milestone;
  onSubmit: (milestoneId: string, deliverables: string[], notes?: string) => void;
}

export const DeliverableSubmission: React.FC<DeliverableSubmissionProps> = ({
  isOpen,
  onClose,
  milestone,
  onSubmit,
}) => {
  const { showToast } = useToast();
  const [mode, setMode] = useState<'link' | 'upload'>('link');
  const [deliverableUrls, setDeliverableUrls] = useState<string[]>(
    milestone.deliverable_urls.length > 0 ? milestone.deliverable_urls : ['']
  );
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFileLabels, setUploadedFileLabels] = useState<string[]>([]);
  const [fileUploadProgress, setFileUploadProgress] = useState<UploadProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      setFileUploadProgress({ status: 'uploading', percent: 0 });
      try {
        const fileUrl = await mediaUploadService.uploadFile(file, file.name, file.type);
        setUploadedFileLabels(prev => [...prev, `[已上传] ${file.name}`]);
        setDeliverableUrls(prev => {
          const filtered = prev.filter(u => u.trim() !== '');
          return [...filtered, fileUrl];
        });
      } catch (error) {
        console.error('Deliverable upload failed:', error);
        showToast(`文件上传失败：${file.name}`, 'error');
      } finally {
        setFileUploadProgress(null);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddUrl = () => {
    setDeliverableUrls([...deliverableUrls, '']);
  };

  const handleRemoveUrl = (index: number) => {
    setDeliverableUrls(deliverableUrls.filter((_, i) => i !== index));
  };

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...deliverableUrls];
    newUrls[index] = value;
    setDeliverableUrls(newUrls);
  };

  const handleSubmit = async () => {
    const validUrls = mode === 'link'
      ? deliverableUrls.filter((url) => url.trim() !== '')
      : deliverableUrls.filter((url) => url.trim() !== '');

    if (validUrls.length === 0) {
      showToast(mode === 'link' ? '请至少添加一个交付物链接' : '请至少上传一个文件', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(milestone.milestone_id, validUrls, notes || undefined);
      onClose();
    } catch (error) {
      console.error('Failed to submit deliverables:', error);
      showToast('提交失败，请重试', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="交付"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? '提交中...' : '提交'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Milestone Info */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-1">{milestone.title}</h4>
          <p className="text-sm text-slate-600">{milestone.description}</p>
          {milestone.submission_count > 0 && (
            <p className="text-xs text-slate-500 mt-2">
              这是第 {milestone.submission_count + 1} 次提交
            </p>
          )}
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-slate-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => { setMode('link'); setUploadedFileLabels([]); }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'link' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <LinkIcon className="w-4 h-4" />
            粘贴链接
          </button>
          <button
            type="button"
            onClick={() => { setMode('upload'); setDeliverableUrls(['']); }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Upload className="w-4 h-4" />
            上传文件
          </button>
        </div>

        {/* Deliverable URLs */}
        {mode === 'link' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              交付物链接 *
            </label>
            <div className="space-y-3">
              {deliverableUrls.map((url, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => handleUrlChange(index, e.target.value)}
                      placeholder="https://example.com/file.zip"
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {deliverableUrls.length > 1 && (
                    <button
                      onClick={() => handleRemoveUrl(index)}
                      className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={handleAddUrl}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              添加更多文件
            </button>
            <p className="mt-2 text-xs text-slate-500">
              支持各类云存储链接（阿里云盘、百度网盘、Google Drive 等）
            </p>
          </div>
        )}

        {/* File Upload */}
        {mode === 'upload' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              上传文件
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.zip,.rar,.7z,.pdf,.psd,.ai"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!!fileUploadProgress}
              className="w-full border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors disabled:opacity-50"
            >
              {fileUploadProgress && fileUploadProgress.status !== 'idle' ? (
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {fileUploadProgress.status === 'uploading' ? `上传中 ${fileUploadProgress.percent}%` : '处理中...'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-slate-500">
                  <Upload className="w-5 h-5" />
                  <span className="text-sm">点击上传文件</span>
                </div>
              )}
            </button>
            {uploadedFileLabels.length > 0 && (
              <div className="mt-2 space-y-1">
                {uploadedFileLabels.map((name, i) => (
                  <div key={i} className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded">✓ {name}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            备注说明（可选）
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="添加关于此次提交的说明..."
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>

        {/* File Upload Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Upload className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">提交须知</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• 请确保所有文件链接有效且可访问</li>
                <li>• 建议使用压缩包形式整理多个文件</li>
                <li>• 提交后客户将收到通知进行审核</li>
                <li>• 如需修改，可在客户审核前重新提交</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
